import React, { useEffect, useState } from 'react';
import './ImageWrapper.scss';
import classNames from 'classnames';

interface IWProps {
  /**
   * Specify size of Image holder.
   * @default small
   */
  size: 'small' | 'medium' | 'large';
  /** Specify relative path or http link of the image
   *
   * @default
   */
  imgUrl: string;
  /*
   * Specify if the image is selected or not
   */
  isSelected?: boolean;
  /** Specify onClick handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Specify width of image */
  width?: number;
  /** Specify height of image */
  height?: number;
  /** Specify optional classnames */
  className?: string;
  /** alt prop for image. */
  alt?: string;
  /** On image error. */
  handleImageError?: () => void;
  /** A boolean value to show the overlay on the image */
  showCenterImgOverlay?: boolean;
  /** A fallback image to show when the image is not loaded */
  fallbackSrc?: string;
  /** A design image to show on hover */
  designImg?: string;
  /** To enable/disble design show on hover */
  swapDImgOnHoverEffect?: boolean;
  /** Mouse hover event */
  onMouseOver?: () => void;
  /** Mouse leave event */
  onMouseLeave?: () => void;
  /** Image focus event */
  onImgFocus?: () => void;
  /** Touch end event */
  onTouchEnd?: () => void;
}

/** Image Wrapper is a container to hold images into fixed set of sizes */
const ImageWrapper: React.FC<IWProps> = ({ ...props }) => {
  const {
    size,
    imgUrl,
    isSelected,
    onClick,
    width,
    height,
    className,
    alt,
    handleImageError,
    showCenterImgOverlay = false,
    fallbackSrc,
    swapDImgOnHoverEffect = false,
    designImg,
    onMouseOver,
    onMouseLeave,
    onTouchEnd,
    onImgFocus,
  } = props;
  const styleClass = classNames(
    `image-wrapper--${size}`,
    { 'is--selected': isSelected },
    className
  );

  const [imageSrc, setImageSrc] = useState(imgUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    setImageSrc(imgUrl);
  }, [imgUrl]);

  const onImageError = () => {
    setError(true);

    if (handleImageError) {
      handleImageError();
    }

    if (fallbackSrc) {
      setImageSrc(fallbackSrc);
    }
  };

  /** Swapping between zoomed design and design with apparel image */
  const swapDesignImg = () => {
    if (!swapDImgOnHoverEffect || !designImg) return;
    if (imageSrc === imgUrl && designImg) setImageSrc(designImg);
    if (imageSrc !== imgUrl) setImageSrc(imgUrl);
  };

  /** Emit mouse over event, also for other funcs used in this component */
  const mouseOverEvent = () => {
    if (typeof onMouseOver === 'function') onMouseOver();
    swapDesignImg();
  };

  /** Emit mouse leave event, also for other funcs used in this component */
  const mouseLeaveEvent = () => {
    if (typeof onMouseLeave === 'function') onMouseLeave();
    swapDesignImg();
  };

  /** Emit focus event on image, also for other funcs used in this component */
  const imgFocusEvent = () => {
    if (typeof onImgFocus === 'function') onImgFocus();
    mouseOverEvent();
  };

  return (
    <button type="button" className={styleClass} onClick={(e) => onClick && onClick(e)}>
      {/* Commented for future reference. Need to spend some time on the lazy wrapper as it is throwing error when we change images multiple times it was showing loader. */}
      {/* <LazyImage
        src={imgUrl}
        alt={alt}
        loading="lazy"
        style={{ width, height }}
        handleImageError={handleImageError}
      /> */}

      <img
        src={imageSrc}
        alt={alt}
        style={{ width, height }}
        loading="lazy"
        onError={onImageError}
        draggable={false}
        className={error ? 'error-image' : ''}
        onMouseOver={mouseOverEvent}
        onFocus={imgFocusEvent}
        onMouseLeave={mouseLeaveEvent}
        onTouchEnd={onTouchEnd}
      />

      {showCenterImgOverlay && <div className="center-shadow-img-overlay" />}
    </button>
  );
};

export default ImageWrapper;
